import {
  createWidgetConversation,
  fetchWidgetConfig,
  checkIsFirstVisit,
  identifyWidgetVisitor,
  ArtisApiError,
} from "./api";
import { AnalyticsModule } from "./analytics";
import { startAutofixProbes } from "./autofixProbe";
import { startBackendErrorCapture } from "./backendErrorCapture";
import { readConfig, findOwnScriptTag, type WidgetPosition } from "./config";
import {
  IdentityStore,
  getOrCreateAnonymousVisitorId,
  type EndUserMetadata,
} from "./identity";
import { saveLiveCall, getLiveCall, clearLiveCall } from "./call-state";
import { AudioPlayer } from "./audio-out";
import { MicCapture } from "./audio-in";
import { LiveClient } from "./ws-client";
import {
  WidgetUI,
  WIDGET_ICON_PRESETS,
  type WidgetIcon,
  type AttentionSide,
} from "./ui";
import { executeDomAction } from "./dom-action";
import { getAgentCursor } from "./agent-cursor";
import { readPageContext } from "./page-context";

// Heuristics for the "idle" attention trigger -- a visitor who's been on
// the page a while, clicking repeatedly in frustration, or scrolling a
// long way without ever clicking anything, is read as "stuck" and shown
// the attention message. Tuned to be generous (avoid false positives on
// a visitor calmly reading a long page) rather than tight.
const ATTENTION_IDLE_DWELL_MS = 45_000;
const ATTENTION_RAGE_CLICK_COUNT = 5;
const ATTENTION_RAGE_CLICK_WINDOW_MS = 10_000;
const ATTENTION_SCROLL_THRESHOLD_PX = 6_000;
// The "first visit" trigger's answer is already known as soon as
// visitor-status resolves -- this delay just keeps the bubble from
// popping before the page has even finished rendering.
const ATTENTION_FIRST_VISIT_DELAY_MS = 3_000;

function resolveIcon(value: string): WidgetIcon {
  return (WIDGET_ICON_PRESETS as readonly string[]).includes(value)
    ? (value as WidgetIcon)
    : "mic";
}

function resolvePosition(value: string): WidgetPosition {
  return value === "bottom-left" ||
    value === "top-right" ||
    value === "top-left" ||
    value === "right-center"
    ? value
    : "bottom-right";
}

function resolveAttentionSide(value: string): AttentionSide {
  return value === "bottom" || value === "left" || value === "right"
    ? value
    : "top";
}

/** The visitor's own browser/OS language preference -- used both to pick
 * which language the attention message speaks in, and (if the visitor
 * then starts a real call) as that call's language override. Deliberately
 * NOT an attempt to detect active page auto-translation (Chrome's
 * built-in translate, browser extensions, etc.): each mechanism marks a
 * translated page differently and none of it is reliably detectable from
 * content scripts across browsers. navigator.language is simpler and
 * answers the question that actually matters here -- "what language does
 * this visitor want to be spoken to in" -- regardless of which language
 * the page's own text happens to render in. */
function detectVisitorLanguage(): string {
  const raw = (navigator.language || navigator.languages?.[0] || "en").toLowerCase();
  return raw.split("-")[0];
}

/** Resolves which configured language to use for the attention message:
 * the visitor's own detected language if the tenant configured one for
 * it, else English, else whichever language key IS configured (in
 * whatever order Object.keys returns), else null if none are configured
 * at all (feature off). The same resolved key is used to look up both
 * the text and the audio URL, so they never mismatch. */
function resolveAttentionLanguage(
  messages: Record<string, string>,
  visitorLanguage: string,
): string | null {
  if (messages[visitorLanguage]) return visitorLanguage;
  if (messages.en) return "en";
  const keys = Object.keys(messages);
  return keys.length > 0 ? keys[0] : null;
}

// Captured synchronously at script-execution time — document.currentScript is
// only valid here, not once init() actually runs (e.g. deferred to DOMContentLoaded).
const scriptTag = findOwnScriptTag();

function init(): void {
  const config = readConfig(scriptTag);
  const identity = new IdentityStore({
    endUserId: scriptTag?.dataset.userId,
    userHash: scriptTag?.dataset.userHash,
    metadata: {
      name: scriptTag?.dataset.userName,
      email: scriptTag?.dataset.userEmail,
      company: scriptTag?.dataset.userCompany,
    },
  });

  // config.position (set via data-position on the <script> tag) is an
  // explicit per-embed override; absent that, the dashboard-configured
  // position (fetched below) wins. Default to bottom-right until then.
  const ui = new WidgetUI(config.position || "bottom-right");
  document.body.appendChild(ui.root);

  const visitorLanguage = detectVisitorLanguage();

  // getUserMedia (the mic) can never be triggered without a direct,
  // trusted gesture on the widget bubble itself -- that's unbypassable
  // browser security, not something this works around. Audio OUTPUT is a
  // softer policy though: browsers allow playing sound once there's been
  // ANY user interaction anywhere on the host page, not necessarily on
  // the widget. This is what lets Artis proactively SPEAK the attention
  // message (one-way) without the visitor ever having touched the
  // widget -- listening in from page load, not scoped to the widget's
  // own elements.
  let audioUnlocked = false;
  let pendingAttentionAudioUrl: string | null = null;
  function armAudioUnlock(): void {
    const unlock = () => {
      audioUnlocked = true;
      if (pendingAttentionAudioUrl) {
        const url = pendingAttentionAudioUrl;
        pendingAttentionAudioUrl = null;
        playAttentionAudio(url);
      }
    };
    document.addEventListener("click", unlock, { capture: true, once: true });
    document.addEventListener("keydown", unlock, { capture: true, once: true });
    document.addEventListener("touchstart", unlock, { capture: true, once: true });
  }
  function playAttentionAudio(url: string): void {
    // Autoplay can still occasionally be refused even after a real gesture
    // (e.g. a stale/old activation, or a browser-specific heuristic) --
    // fail silently and leave the text bubble as the fallback rather than
    // surfacing an error for a purely decorative enhancement.
    new Audio(url).play().catch(() => {});
  }
  armAudioUnlock();

  // Whichever attention trigger fires first wins -- there's only one
  // message, so once shown there's nothing more for the others to do.
  let attentionShown = false;
  let attentionTimers: Array<ReturnType<typeof setTimeout>> = [];
  let attentionCleanup: (() => void) | null = null;

  function clearAttentionTriggers(): void {
    for (const t of attentionTimers) clearTimeout(t);
    attentionTimers = [];
    attentionCleanup?.();
    attentionCleanup = null;
  }

  function fireAttention(message: string, audioUrl?: string): void {
    if (attentionShown) return;
    attentionShown = true;
    clearAttentionTriggers();
    ui.showAttentionMessage(message);
    if (!audioUrl) return;
    if (audioUnlocked) {
      playAttentionAudio(audioUrl);
    } else {
      // Spoken late (whenever the first interaction eventually happens)
      // still makes sense -- the text bubble is already visible by then.
      pendingAttentionAudioUrl = audioUrl;
    }
  }

  // Dwell timer + rage-click counter + cumulative-scroll-without-click,
  // any one of which reads as "this visitor looks stuck" and fires the
  // same message.
  function armIdleTrigger(message: string, audioUrl?: string): void {
    attentionTimers.push(
      setTimeout(() => fireAttention(message, audioUrl), ATTENTION_IDLE_DWELL_MS),
    );

    let clickTimestamps: number[] = [];
    let scrolledSinceClick = 0;
    let lastScrollY = window.scrollY;

    const onClick = () => {
      const now = Date.now();
      clickTimestamps.push(now);
      clickTimestamps = clickTimestamps.filter(
        (t) => now - t <= ATTENTION_RAGE_CLICK_WINDOW_MS,
      );
      scrolledSinceClick = 0;
      if (clickTimestamps.length >= ATTENTION_RAGE_CLICK_COUNT) {
        fireAttention(message, audioUrl);
      }
    };
    const onScroll = () => {
      const y = window.scrollY;
      scrolledSinceClick += Math.abs(y - lastScrollY);
      lastScrollY = y;
      if (scrolledSinceClick >= ATTENTION_SCROLL_THRESHOLD_PX) {
        fireAttention(message, audioUrl);
      }
    };

    document.addEventListener("click", onClick, { capture: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    attentionCleanup = () => {
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("scroll", onScroll);
    };
  }

  fetchWidgetConfig(config.apiUrl, config.agentId).then((widgetConfig) => {
    if (!widgetConfig) {
      // No config to apply (fetch failed, or the agent has none yet) — stay
      // on the built-in defaults rather than leaving the bubble stuck
      // unclickable forever.
      ui.setReady(true);
      return;
    }
    // Only ever a fallback: if the host page already called identify() or
    // set data-user-id/data-user-hash before this resolved, that real
    // identity wins — this never overwrites it. Public/anonymous sites
    // (no logged-in end users) are the only case this applies to, and only
    // when the agent's own dashboard config opted in.
    if (widgetConfig.public_identity && !identity.get()) {
      identity.set(getOrCreateAnonymousVisitorId(), "");
    }

    if (widgetConfig.analytics_enabled) {
      analytics = new AnalyticsModule({
        apiUrl: config.apiUrl,
        agentId: config.agentId,
        identity,
        waitForConsent: widgetConfig.analytics_wait_for_consent,
      });
      analytics.start();
      if (widgetConfig.backend_error_capture_enabled) {
        startBackendErrorCapture(config.apiUrl, analytics);
      }
    }
    startAutofixProbes(widgetConfig, analytics);

    ui.applyAppearance({
      title: widgetConfig.public_name || "Talk to us",
      textColor: widgetConfig.text_color,
      color: widgetConfig.color,
      color2: widgetConfig.color2,
      gradient: widgetConfig.gradient,
      mode: widgetConfig.mode,
      style: widgetConfig.style,
      icon: resolveIcon(widgetConfig.icon),
      logoUrl: widgetConfig.logo_url,
      logoScale: widgetConfig.logo_scale,
      position: config.position || resolvePosition(widgetConfig.position),
    });

    // The page may have just reloaded mid-call (a real navigation, not a
    // single-page app's client-side routing) — if so, reconnect to the
    // same conversation instead of leaving the call dropped. Needs
    // identity to already be resolved (the public_identity fallback right
    // above, or the host's own identify()/data-user-* already in place by
    // now) — if there's genuinely no identity, there's nothing to resume.
    const savedConversationId = getLiveCall(config.agentId);
    if (savedConversationId && identity.get()) {
      startCall({ conversationId: savedConversationId, resuming: true });
      return;
    }

    const resolvedLang = resolveAttentionLanguage(
      widgetConfig.attention_messages || {},
      visitorLanguage,
    );
    const message = resolvedLang
      ? widgetConfig.attention_messages[resolvedLang]?.trim()
      : undefined;
    const audioUrl = resolvedLang
      ? widgetConfig.attention_audio_urls?.[resolvedLang]
      : undefined;
    if (message) {
      ui.setAttentionAppearance(
        resolveAttentionSide(widgetConfig.attention_position),
        widgetConfig.attention_width,
      );
      if (widgetConfig.attention_trigger_idle) {
        armIdleTrigger(message, audioUrl);
      }
      if (widgetConfig.attention_trigger_first_visit) {
        // Only resolvable client-side for the HMAC/anonymous identity
        // paths -- a JWT (idToken) identity's end_user_id lives inside
        // the token, decoded only server-side, so this trigger simply
        // can't fire for that identity mode.
        const endUserId = identity.get()?.endUserId;
        if (endUserId) {
          checkIsFirstVisit(config.apiUrl, config.agentId, endUserId).then(
            (isNew) => {
              if (!isNew) return;
              attentionTimers.push(
                setTimeout(
                  () => fireAttention(message, audioUrl),
                  ATTENTION_FIRST_VISIT_DELAY_MS,
                ),
              );
            },
          );
        }
      }
    }
  });

  let analytics: AnalyticsModule | null = null;

  const mic = new MicCapture();
  const player = new AudioPlayer();
  let client: LiveClient | null = null;
  let ending = false;

  function teardown(): void {
    ending = false;
    mic.stop();
    player.close();
    client?.close();
    client = null;
    getAgentCursor().hide();
    // Whether the call ended normally, errored, or was a resume attempt —
    // once it's over there's nothing left to resume on the next page load.
    clearLiveCall(config.agentId);
  }

  ui.onEnd = () => {
    ending = true;
    client?.close();
  };

  // opts.conversationId resumes an existing conversation (the page reloaded
  // mid-call) instead of minting a new one — same server-side resume path
  // already used for a dropped network connection, see ai-service's
  // on_connect. opts.resuming additionally keeps a dead/expired conversation
  // from surfacing a scary error on a page the visitor never saw a call
  // start on: fail silently and let them start a fresh one if they want.
  async function startCall(opts?: {
    conversationId?: string;
    resuming?: boolean;
  }): Promise<void> {
    const id = identity.get();
    if (!id) {
      if (opts?.resuming) return;
      ui.setState(
        "error",
        "This chat isn't set up yet — ask your site's engineering team to configure it.",
      );
      return;
    }

    ui.setState("connecting");
    let sessionLive = false;
    try {
      let conversationId = opts?.conversationId;
      if (!conversationId) {
        const result = await createWidgetConversation(
          config.apiUrl,
          config.agentId,
          id,
          visitorLanguage,
        );
        conversationId = result.conversationId;
        // Every analytics event sent from here on (this page, this
        // session) now carries these real, verified fields -- same bag
        // identify()'s own traits already flow through.
        if (result.identityExtra) identity.mergeMetadata(result.identityExtra);
        // ...and, for a JWT identity, now tagged with the REAL visitor id
        // instead of the anon one -- see identity.ts's setResolvedVisitorId.
        if (result.endUserId) identity.setResolvedVisitorId(result.endUserId);
      }
      saveLiveCall(config.agentId, conversationId);

      client = new LiveClient(conversationId, {
        onAudio: (base64) => {
          ui.setSpeaking(true);
          player.playChunk(base64);
        },
        onTranscription: (role) => {
          // The user's turn just got transcribed — the agent is now
          // processing it. Cleared by the first audio chunk (setSpeaking)
          // or turn-complete/interrupted below if it never speaks at all.
          if (role === "user") ui.setThinking(true);
        },
        onTurnComplete: () => {
          ui.setSpeaking(false);
          ui.setThinking(false);
        },
        onInterrupted: () => {
          ui.setSpeaking(false);
          ui.setThinking(false);
          player.interrupt();
        },
        onSessionTimer: (remaining) => {
          if (!sessionLive) {
            sessionLive = true;
            ui.setState("live");
            getAgentCursor().show();
            // A silent resume never had its panel opened on this page load
            // — bring it back so the visitor can see the call is still on.
            if (opts?.resuming) ui.openPanel();
          }
          ui.setTimer(remaining);
        },
        onSessionEnded: () => {
          ui.setState("ended");
          teardown();
        },
        onError: (message) => {
          if (opts?.resuming && !sessionLive) {
            // Dead/expired conversation on a page the visitor never saw a
            // call start on — go back to idle quietly instead of surfacing
            // an alarming error for something they didn't do.
            teardown();
            ui.setState("idle");
            return;
          }
          ui.setState("error", message);
          teardown();
        },
        onClose: (code) => {
          if (opts?.resuming && !sessionLive) {
            teardown();
            ui.setState("idle");
            return;
          }
          if (ending || code === 1000) {
            ui.setState("ended");
          } else {
            ui.setState("error", "The call ended unexpectedly.");
          }
          teardown();
        },
        onDomActionCall: (payload) => {
          executeDomAction(payload).then((result) => {
            client?.sendDomActionResult(result);
          });
        },
        onPageContextCall: (payload) => {
          const result = readPageContext(payload.call_id, ui.root);
          client?.sendPageContextResult(result);
        },
      });
      // Same proof of identity already verified in POST /widget/conversations
      // above — the WS connection is separate and doesn't inherit that auth,
      // so it's re-checked server-side against this same token.
      const wsToken = id.idToken ?? `${id.endUserId}:${id.userHash}`;
      client.connect(config.apiUrl, config.agentId, wsToken);

      // The session is already live via WS above — a denied/pending mic
      // shouldn't kill it, the visitor can still hear the agent speak.
      try {
        await mic.start((base64) => client?.sendAudioChunk(base64));
      } catch {
        ui.setMicWarning(
          "Microphone unavailable — you can listen, but the agent won't hear you.",
        );
      }
    } catch (err) {
      if (opts?.resuming) {
        teardown();
        ui.setState("idle");
        return;
      }
      const message =
        err instanceof ArtisApiError ? err.message : "Could not start the call.";
      ui.setState("error", message);
      teardown();
    }
  }

  ui.onOpen = () => {
    clearAttentionTriggers();
    ui.hideAttentionMessage();
    startCall();
  };

  window.ArtisWidget = {
    identify(endUserId: string, userHash: string, metadata?: EndUserMetadata) {
      identity.set(endUserId, userHash, metadata);
    },
    identifyWithToken(idToken: string, metadata?: EndUserMetadata) {
      identity.setToken(idToken, metadata);
      // Resolved as its own event, same timing as PostHog/Intercom/
      // Segment's identify() -- available to every analytics event sent
      // from here on, not gated behind the visitor ever starting a call
      // (see POST /widget/conversations' own, separate identity_extra
      // resolution for that path).
      identifyWidgetVisitor(config.apiUrl, config.agentId, idToken).then((result) => {
        if (!result) return;
        if (result.identityExtra) identity.mergeMetadata(result.identityExtra);
        if (result.endUserId) identity.setResolvedVisitorId(result.endUserId);
      });
    },
    open() {
      ui.openPanel();
    },
    grantAnalyticsConsent() {
      analytics?.grantConsent();
    },
    reset() {
      identity.reset();
    },
  };
}

declare global {
  interface Window {
    ArtisWidget: {
      identify: (
        endUserId: string,
        userHash: string,
        metadata?: EndUserMetadata,
      ) => void;
      identifyWithToken: (idToken: string, metadata?: EndUserMetadata) => void;
      open: () => void;
      grantAnalyticsConsent: () => void;
      // Call on logout -- clears identity and the persisted anonymous
      // visitor id, so a second real person on a shared device/browser
      // doesn't keep inheriting the first person's anon-id history. See
      // identity.ts's IdentityStore.reset().
      reset: () => void;
    };
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
